'use client'

import ArrowForward from '@/app/_components/Icon/ArrowForward'
import Search from '@/app/_components/Icon/Search'
import { Button } from '@/app/_primitives/Button'
import { CloseCircleFilled } from '@/app/_primitives/icons/CloseCircleFilled'
import { cn } from '@/app/utils'
import { CharacterSearchSearchParams } from '@/features/character-search/search-params/client'
import { recentView } from '@/libraries/recent-view'
import { useCurrentLocale, useScopedI18n } from '@/locales/client'

import { useRouter } from 'next/navigation'
import { useQueryState } from 'nuqs'
import {
  Dispatch,
  FormEventHandler,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
  useTransition,
} from 'react'
import { twMerge } from 'tailwind-merge'

export function CharacterSearchInput({
  variant = 'default',
  className,
  inSearchPage,
  inHeader,
  withoutArrowIcon,
  setPopoverOpen,
}: {
  variant?: 'default' | 'lg' | 'sm'
  className?: string
  inSearchPage?: boolean
  inHeader?: boolean
  withoutArrowIcon?: boolean
  setPopoverOpen?: Dispatch<SetStateAction<boolean>>
}) {
  const t = useScopedI18n('common')
  const [pending, startTransition] = useTransition()
  const [searchKeyword] = useQueryState(
    'keyword',
    CharacterSearchSearchParams.keyword.withOptions({
      shallow: false,
    }),
  )
  const [keyword, setKeyword] = useState(searchKeyword ?? '')
  const currentLang = useCurrentLocale()

  // TODO: why??? why not use translations?
  // TODO: need to fix this
  const placeholder =
    currentLang === 'ko'
      ? '동물 캐릭터'
      : currentLang === 'ja'
        ? '動物キャラクター'
        : 'animal characters'

  useEffect(() => {
    if (!inSearchPage) return

    setKeyword(searchKeyword ?? '')
  }, [searchKeyword, inSearchPage])

  const router = useRouter()
  const handleSearch = useCallback(
    (keyword: string) => {
      startTransition(() => {
        const kwURI = encodeURIComponent(keyword)

        router.push(
          keyword === ''
            ? `/search/characters`
            : `/search/characters?keyword=${kwURI}`,
        )
      })
    },
    [router],
  )

  const handleSubmit: FormEventHandler = useCallback(
    (e) => {
      e.preventDefault()
      let kw = keyword.trim()

      if (inSearchPage) {
        if (kw === '') return handleSearch(kw)

        recentView.addKeyword(kw)
        handleSearch(kw)
      } else handleSearch(kw)
    },
    [inSearchPage, handleSearch, keyword],
  )

  const inputCName = twMerge(
    `c-b3 scale-[0.875] lg:scale-100 lg:min-w-445 w-full rounded-100 bg-white pl-62 pr-52 outline-none`,
    variant === 'lg' ? 'h-72' : variant === 'sm' ? 'h-48' : 'h-56',
  )

  return (
    <form
      id="search-form"
      className={twMerge('relative', className)}
      onSubmit={handleSubmit}
      data-pending={pending ? '' : undefined}>
      <div className="relative w-full scale-[1.143] lg:scale-100">
        <input
          className={inputCName}
          type="search"
          name="search"
          placeholder={placeholder}
          value={keyword}
          onChange={(e) => {
            setKeyword(e.target.value)
          }}
          autoComplete="off"
          id="search-input"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.currentTarget.form?.requestSubmit()
              if (setPopoverOpen) {
                setPopoverOpen(false)
              }
            }
          }}
        />
      </div>
      <div
        className={cn('absolute left-24 top-0 flex h-full items-center', {
          'left-20': inHeader,
        })}>
        <Search />
      </div>
      <div
        className={cn('absolute right-[18px] top-1/2 -translate-y-1/2', {
          hidden: keyword === '' && inSearchPage,
        })}>
        <CloseCircleFilled
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()

            handleSearch('')
          }}
          className="size-[20px] cursor-pointer fill-icon-subtle"
        />
      </div>
      <div
        className={cn('absolute right-0 top-0', {
          hidden: keyword !== '' && inSearchPage,
        })}>
        <Button
          type="button"
          color="butterScotch"
          size={variant}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()

            handleSubmit(e)
            setPopoverOpen?.(false)
          }}
          className={cn(
            'search-button',
            'flex w-136 items-center justify-center lg:w-166',
            { '!h-72': variant === 'lg' },
            { '!h-56': variant === 'default' },
            { '!h-48': variant === 'sm' },
            { '!c-h8 !w-108': inSearchPage && !inHeader },
            { '!c-h8 !w-89': inHeader },
          )}>
          {!inSearchPage ? (
            <>
              {t('searchLabel')}
              {!withoutArrowIcon && <ArrowForward />}
            </>
          ) : (
            t('searchLabel')
          )}
        </Button>
      </div>
    </form>
  )
}
